/**
 * RuleDryRunPanel (FE-4, PHASE1 §7.4). Non-mutating preview of how a rule's
 * conditions would land on existing transactions. Headline reads
 * "würde N treffen, M unberührt" (would_match / user_protected). A sample of
 * matched transactions is listed underneath.
 */
import { PlayCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { useRuleDryRun } from '../../lib/hooks/useRules.ts';
import { formatDate } from '../../lib/format.ts';
import type { RuleCondition, RuleMatchMode } from '../../lib/types.ts';

export interface RuleDryRunPanelProps {
  matchMode: RuleMatchMode;
  /** Cleaned conditions (empty-value rows already stripped). */
  conditions: RuleCondition[];
}

export function RuleDryRunPanel({ matchMode, conditions }: RuleDryRunPanelProps) {
  const dryRun = useRuleDryRun();
  const disabled = conditions.length === 0 || dryRun.isPending;

  function run() {
    dryRun.mutate({ match_mode: matchMode, conditions });
  }

  const result = dryRun.data;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
          Vorschau
        </span>
        <Button variant="secondary" size="sm" onClick={run} disabled={disabled}>
          <PlayCircle className="h-4 w-4" />
          {dryRun.isPending ? 'Prüfe …' : 'Testen'}
        </Button>
      </div>

      {conditions.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Füge eine Bedingung hinzu, um die Vorschau zu testen.
        </p>
      ) : null}

      {dryRun.isError ? (
        <p className="text-xs text-rose-600 dark:text-rose-400">
          {dryRun.error instanceof Error ? dryRun.error.message : 'Vorschau fehlgeschlagen.'}
        </p>
      ) : null}

      {result ? (
        <div className="space-y-2">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Würde{' '}
            <span className="font-semibold text-slate-900 tabular-nums dark:text-slate-100">
              {result.would_match}
            </span>{' '}
            treffen,{' '}
            <span className="font-semibold text-slate-900 tabular-nums dark:text-slate-100">
              {result.user_protected}
            </span>{' '}
            unberührt.
          </p>
          <p className="text-xs text-slate-400 tabular-nums dark:text-slate-500">
            {result.would_categorize} Transaktionen würden neu kategorisiert.
          </p>
          {result.samples.length > 0 ? (
            <ul className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-800/70 dark:border-slate-800/70">
              {result.samples.map((sample) => (
                <li
                  key={sample.id}
                  className="flex items-center justify-between gap-3 py-2 text-sm"
                >
                  <span className="min-w-0 truncate text-slate-600 dark:text-slate-400">
                    {sample.counterparty_name ?? 'Ohne Empfänger'}
                  </span>
                  <span className="flex shrink-0 items-center gap-3">
                    <span className="text-xs text-slate-400 tabular-nums dark:text-slate-500">
                      {formatDate(sample.booking_date)}
                    </span>
                    <AmountText value={sample.amount} />
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
